import React, { useState } from 'react';
import { useCartStore, useAuthStore } from '../store';
import { betsAPI } from '../services/api';
import { FaTrash, FaShoppingCart, FaTimes, FaLink } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Cart: React.FC = () => {
  const { items, removeItem, clearCart, totalOdds } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stakes, setStakes] = useState<Record<number, number>>({});
  const [parlayStake, setParlayStake] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [betMode, setBetMode] = useState<'individual' | 'combinada'>('individual');

  const getStake = (idx: number) => stakes[idx] ?? 10;
  const setStake = (idx: number, val: number) => setStakes(prev => ({ ...prev, [idx]: val }));

  const combinedOdds = items.reduce((acc, item) => acc * (item.suggestion.cuota_esperada || 1), 1);
  const parlayPotentialWin = parlayStake * combinedOdds;

  const placeBets = async () => {
    if (!user) { navigate('/login'); return; }
    if (items.length === 0) { toast.error('El carrito está vacío'); return; }

    setLoading(true);
    try {
      if (betMode === 'individual') {
        let successCount = 0;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const stake = getStake(i);
          try {
            await betsAPI.create({
              encuentro_id: item.match.id,
              tipo_apuesta: item.suggestion.tipo,
              descripcion: item.suggestion.descripcion,
              seleccion: item.suggestion.seleccion,
              cantidad_apostada: stake,
              cuota_bloqueada: item.suggestion.cuota_esperada
            });
            successCount++;
          } catch (err: any) {
            toast.error(`Error en apuesta ${i + 1}: ${err.response?.data?.error}`);
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} apuesta(s) creada(s)`);
          clearCart();
          navigate('/mis-apuestas');
        }
      } else {
        // Parlay mode
        if (items.length < 2) {
          toast.error('El parlay necesita al menos 2 selecciones');
          setLoading(false);
          return;
        }
        const picks = items.map(item => ({
          encuentro_id: item.match.id,
          match_label: `${item.match.local_nombre} vs ${item.match.visitante_nombre}`,
          tipo: item.suggestion.tipo,
          descripcion: item.suggestion.descripcion,
          seleccion: item.suggestion.seleccion,
          cuota: item.suggestion.cuota_esperada,
        }));
        await betsAPI.createParlay({ picks, cantidad_apostada: parlayStake });
        toast.success(`Parlay creado — cuota combinada ${combinedOdds.toFixed(2)}x`);
        clearCart();
        navigate('/mis-apuestas');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al procesar apuestas');
    } finally {
      setLoading(false);
    }
  };

  const totalIndividualStake = items.reduce((a, _, i) => a + getStake(i), 0);
  const totalIndividualWin = items.reduce((acc, item, idx) => acc + getStake(idx) * item.suggestion.cuota_esperada, 0);

  return (
    <div className="min-h-screen bg-slate-950 pt-20 px-4 pb-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <FaShoppingCart className="text-indigo-400" size={24} />
          <h1 className="text-2xl font-bold text-white">Carrito de Apuestas</h1>
          {items.length > 0 && (
            <span className="bg-indigo-600 text-white text-xs rounded-full px-2 py-0.5">{items.length}</span>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <FaShoppingCart className="text-slate-600 mx-auto mb-4" size={48} />
            <p className="text-slate-400">Tu carrito está vacío</p>
            <button onClick={() => navigate('/apuestas-sugeridas')} className="btn-primary mt-4">
              Ver Apuestas Sugeridas
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3">
              {/* Mode Toggle */}
              <div className="card flex gap-2">
                <button
                  onClick={() => setBetMode('individual')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${betMode === 'individual' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-400'}`}
                >
                  Apuestas Individuales
                </button>
                <button
                  onClick={() => setBetMode('combinada')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${betMode === 'combinada' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400'}`}
                >
                  <FaLink className="inline mr-1" size={11} />
                  Parlay ({combinedOdds.toFixed(2)}x)
                </button>
              </div>

              {betMode === 'combinada' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-300">
                  <strong>Parlay combinado:</strong> todas las selecciones deben ganar para cobrar. La cuota total es el producto de todas las cuotas individuales.
                </div>
              )}

              {items.map((item, idx) => (
                <div key={idx} className={`card ${betMode === 'combinada' ? 'border-emerald-500/20' : ''}`}>
                  {betMode === 'combinada' && (
                    <div className="flex items-center gap-1 mb-2 text-xs text-emerald-400">
                      <FaLink size={10} />
                      <span>Pick {idx + 1} de {items.length}</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-slate-400 mb-1">{item.match.local_nombre} vs {item.match.visitante_nombre}</div>
                      <div className="font-medium text-white text-sm">{item.suggestion.descripcion}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500">{item.suggestion.tipo}</span>
                        <span className={`risk-${item.suggestion.riesgo?.toLowerCase()} text-xs`}>{item.suggestion.riesgo}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-yellow-400 font-mono font-bold">{(item.suggestion.cuota_esperada || 1).toFixed(2)}</div>
                        <div className="text-xs text-emerald-400">{item.suggestion.probabilidad_acierto}%</div>
                      </div>
                      <button onClick={() => removeItem(idx)} className="text-slate-500 hover:text-red-400 transition-colors p-1">
                        <FaTimes size={14} />
                      </button>
                    </div>
                  </div>

                  {betMode === 'individual' && (
                    <div className="mt-3 flex items-center gap-3">
                      <label className="text-xs text-slate-400">Monto ($):</label>
                      <input
                        type="number"
                        min={1}
                        value={getStake(idx)}
                        onChange={(e) => setStake(idx, parseFloat(e.target.value) || 0)}
                        className="input-field w-24 text-sm py-1.5"
                      />
                      <span className="text-xs text-emerald-400">
                        Posible: ${(getStake(idx) * item.suggestion.cuota_esperada).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {/* Parlay stake input */}
              {betMode === 'combinada' && (
                <div className="card border-emerald-500/30">
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-slate-300 font-medium">Monto del parlay ($):</label>
                    <input
                      type="number"
                      min={1}
                      value={parlayStake}
                      onChange={(e) => setParlayStake(parseFloat(e.target.value) || 0)}
                      className="input-field w-28 text-sm py-1.5"
                    />
                    <div className="text-sm text-emerald-400 font-bold">
                      Posible: ${parlayPotentialWin.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="space-y-4">
              <div className="card">
                <h3 className="font-semibold text-white mb-4">Resumen</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Selecciones:</span>
                    <span className="text-white">{items.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Modo:</span>
                    <span className={betMode === 'combinada' ? 'text-emerald-400 font-medium' : 'text-white'}>
                      {betMode === 'individual' ? 'Individual' : 'Parlay'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total apostado:</span>
                    <span className="text-white">
                      ${betMode === 'individual' ? totalIndividualStake.toFixed(2) : parlayStake.toFixed(2)}
                    </span>
                  </div>
                  {betMode === 'combinada' && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cuota combinada:</span>
                      <span className="text-yellow-400 font-mono font-bold">{combinedOdds.toFixed(2)}x</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2">
                    <span className="text-slate-400">Ganancia posible:</span>
                    <span className="text-emerald-400 font-bold">
                      ${betMode === 'individual' ? totalIndividualWin.toFixed(2) : parlayPotentialWin.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <button onClick={placeBets} disabled={loading} className={`w-full ${betMode === 'combinada' ? 'bg-emerald-600 hover:bg-emerald-500' : 'btn-success'} text-white font-semibold py-2 px-4 rounded-lg transition-all disabled:opacity-50`}>
                    {loading ? 'Procesando...' : betMode === 'combinada' ? `Apostar Parlay ${combinedOdds.toFixed(2)}x` : 'Confirmar Apuestas'}
                  </button>
                  <button onClick={clearCart} className="btn-secondary w-full flex items-center justify-center gap-2 text-sm">
                    <FaTrash size={12} />
                    Limpiar Carrito
                  </button>
                </div>
              </div>

              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-3 text-xs text-yellow-300">
                ⚠️ Las apuestas son solo para entretenimiento. Juega responsablemente.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
